import { ApplicationQuoteType, hasValue } from '@amsanalytics/react-shared-library';
import moment from 'moment';
import { IApplication } from '../Interfaces/Application/IApplication';
import { IApplicationLeaseQuote } from '../Interfaces/Application/IApplicationLeaseQuote';
import { ILeaseAmortizationSchedulePayment } from '../Interfaces/Application/ILeaseAmortizationSchedulePayment';
import { IQuotePayType } from '../Interfaces/Common/IQuotePayType';
import { getLeaseQuoteTotalOfPaymentsCalc } from './appUtils';
import { debtToIncome, getConvertedMonthlyPaymentAmount, getIncomeAndDebtBreakdown } from './quoteUtils';
import { getPaymentToIncomeRatioWithPayment } from './utils';

// Lease values for business rules (context.utilities). Each returns 0, [] or undefined when the app is not a lease.

export interface LeaseDeferredDownPayment {
  slot: number;
  amount: number;
  date: Date | string | undefined;
}

export const getLeaseQuote = (application: IApplication | undefined): IApplicationLeaseQuote | undefined =>
  application?.quoteType === ApplicationQuoteType.Lease ? application.applicationLeaseQuote ?? undefined : undefined;

export const getLeaseFullPayment = (application: IApplication | undefined): number =>
  getLeaseQuote(application)?.fullPaymentAmount ?? 0;

export const getLeaseBasePayment = (application: IApplication | undefined): number =>
  getLeaseQuote(application)?.basePaymentAmount ?? 0;

/** (Base payment + tax portion) × solved number of payments, as the quoter shows it. */
export const getLeaseTotalOfPayments = (application: IApplication | undefined): number =>
  getLeaseQuoteTotalOfPaymentsCalc(getLeaseQuote(application)) ?? 0;

/** Every lease fee, acquisition and disposition included, as the quoter's Total Fees. */
export const getLeaseTotalFees = (application: IApplication | undefined): number => {
  const quote = getLeaseQuote(application);
  if (!quote) {
    return 0;
  }
  return [
    quote.gapFee, quote.docFee, quote.titleFee, quote.licenseFee, quote.batteryFee, quote.transferFee,
    quote.deliveryFee, quote.serviceWarranty, quote.dispositionFee, quote.acquisitionFee,
    quote.customFee1, quote.customFee2, quote.customFee3,
  ].reduce((total: number, fee) => total + (fee ?? 0), 0);
};

export const getLeaseCashDown = (application: IApplication | undefined): number =>
  getLeaseQuote(application)?.cashDown ?? 0;

/** The deferred down payments that have an amount, in slot order. */
export const getLeaseDeferredDownPayments = (application: IApplication | undefined): LeaseDeferredDownPayment[] => {
  const quote = getLeaseQuote(application);
  if (!quote) {
    return [];
  }
  return [1, 2, 3, 4, 5, 6]
    .map((slot) => ({ slot, amount: quote[`deferredPmtAmount${slot}`], date: quote[`deferredPmtDate${slot}`] ?? undefined }))
    .filter((payment) => hasValue(payment.amount) && payment.amount > 0);
};

export const getLeaseTotalDeferredDown = (application: IApplication | undefined): number =>
  getLeaseDeferredDownPayments(application).reduce((total, payment) => total + payment.amount, 0);

/** Payment-to-income with the lease full payment, converted to monthly by the pay type. */
export const getLeasePaymentToIncome = (application: IApplication | undefined, quotePayTypes: IQuotePayType[]): number =>
  getLeaseQuote(application) ? getPaymentToIncomeRatioWithPayment(application, quotePayTypes).paymentToIncomeRatio : 0;

/**
 * Debt-to-income with the lease full payment, converted to monthly by the pay type, added to the budget's
 * monthly debt (getIncomeAndDebtBreakdown). Pass the rule context's excludePaymentFromDebtToIncome.
 */
export const getLeaseDebtToIncome = (
  application: IApplication | undefined,
  quotePayTypes: IQuotePayType[],
  excludePaymentFromDebtToIncome = false
): number => {
  const quote = getLeaseQuote(application);
  if (!quote || !application?.applicationBudget) {
    return 0;
  }
  const { totalDebtUsed, totalIncomeUsed } = getIncomeAndDebtBreakdown(application.applicationBudget);
  const paymentsPerYear = quotePayTypes.find((qpt) => qpt.id?.toLowerCase() === quote.paymentFrequencyId?.toLowerCase())?.paymentsPerYear ?? 12;
  const monthlyPayment = excludePaymentFromDebtToIncome ? 0 : getConvertedMonthlyPaymentAmount(paymentsPerYear, getLeaseFullPayment(application));
  return debtToIncome(totalDebtUsed + monthlyPayment, totalIncomeUsed);
};

// Days between payments by payments per year; monthly steps by calendar month.
const DAYS_BETWEEN_PAYMENTS: Record<number, number> = { 24: 15, 26: 14, 52: 7 };

/**
 * Date of the last payment, stepped from the first payment date as the server's GetCalculatedMaturityDate
 * does (monthly by calendar month, semi-monthly 15 days, bi-weekly 14, weekly 7, otherwise the first date).
 */
export const getLeaseMaturityDate = (application: IApplication | undefined, quotePayTypes: IQuotePayType[]): Date | undefined => {
  const quote = getLeaseQuote(application);
  if (!quote?.firstPmtDate || !(quote.numberOfPmts > 0)) {
    return undefined;
  }
  const first = moment(quote.firstPmtDate);
  const paymentsAfterFirst = quote.numberOfPmts - 1;
  const paymentsPerYear = quotePayTypes.find((qpt) => qpt.id?.toLowerCase() === quote.paymentFrequencyId?.toLowerCase())?.paymentsPerYear ?? 0;
  if (paymentsPerYear === 12) {
    return first.add(paymentsAfterFirst, 'months').toDate();
  }
  const days = DAYS_BETWEEN_PAYMENTS[paymentsPerYear];
  return days ? first.add(days * paymentsAfterFirst, 'days').toDate() : first.startOf('day').toDate();
};

/** The last good Solve's payment schedule, by payment number. */
export const getLeaseAmortizationSchedule = (application: IApplication | undefined): ILeaseAmortizationSchedulePayment[] =>
  [...(getLeaseQuote(application)?.amortizationSchedulePayments ?? [])].sort((a, b) => a.paymentNumber - b.paymentNumber);
